/**
 * CP-9.3 — Phase 9 Disputes surface (filer + counterparty roles).
 *
 * Exports:
 *  - DisputesInbox       /app/disputes
 *  - DisputeDetail       /app/disputes/:id
 *  - CounterpartyInbox   /app/counterparty/disputes
 *  - CounterpartyDetail  /app/counterparty/disputes/:id
 *
 * All endpoints respect r2 §17.4 visibility rules:
 *  - filer detail uses /me/disputes/:id (full); counterparty uses
 *    /me/counterparty/disputes/:id (restricted view that omits internal admin
 *    notes and the rater_id for rating disputes).
 *  - Evidence ledger is append-only: clients cannot edit or delete; admins
 *    can redact via the admin route.
 *  - Status timeline, remediation history, credit-memo refs, financial hold
 *    badges, and resolution summary are all rendered when present.
 */
import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, FileText, Lock, AlertTriangle, CheckCircle2,
          Clock, Inbox, Paperclip, RefreshCw, DollarSign, Shield } from "lucide-react";
import { api } from "@/lib/api";
import { Layout } from "@/components/Layout";

const STATE_BADGE = {
  proposed: { color: "bg-amber-50 text-amber-800 border-amber-300", label: "Proposed" },
  under_review: { color: "bg-blue-50 text-blue-800 border-blue-300", label: "Under review" },
  rejected: { color: "bg-rose-50 text-rose-800 border-rose-300", label: "Rejected" },
  denied: { color: "bg-rose-50 text-rose-800 border-rose-300", label: "Denied" },
  upheld_full: { color: "bg-emerald-50 text-emerald-800 border-emerald-300", label: "Upheld (full)" },
  upheld_partial: { color: "bg-emerald-50 text-emerald-800 border-emerald-300", label: "Upheld (partial)" },
  withdrawn: { color: "bg-slate-50 text-slate-800 border-slate-300", label: "Withdrawn" },
  stale: { color: "bg-slate-50 text-slate-800 border-slate-300", label: "Stale (SLA)" },
};

function StateBadge({ state }) {
  const s = STATE_BADGE[state] || { color: "bg-slate-100 text-slate-800", label: state };
  return (
    <span data-testid={`dispute-state-${state}`}
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${s.color}`}>
      {s.label}
    </span>
  );
}

function fmt(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("en-CA", { year: "numeric", month: "short",
    day: "numeric", hour: "2-digit", minute: "2-digit" });
}

// ============================================================================
// Filer inbox
// ============================================================================

export function DisputesInbox() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  useEffect(() => {
    (async () => {
      try {
        const r = await api("/me/disputes?limit=50");
        setRows(r.data || []);
      } catch (e) {
        toast.error(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);
  return (
    <Layout>
      <div className="max-w-5xl mx-auto p-6" data-testid="disputes-inbox-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900">My disputes</h1>
            <p className="text-sm text-slate-500 mt-1">
              Disputes you filed across bookings, invoices, payouts, ratings and cancel fees.
            </p>
          </div>
          <Link to="/app/counterparty/disputes"
                data-testid="link-counterparty-inbox"
                className="text-sm text-blue-600 hover:underline">
            View disputes filed against me →
          </Link>
        </div>
        {loading && <div className="text-slate-500">Loading…</div>}
        {!loading && rows.length === 0 && (
          <div data-testid="empty-disputes" className="text-center py-16 text-slate-400">
            <Inbox className="mx-auto h-10 w-10 mb-2" />
            <div>No disputes yet.</div>
          </div>
        )}
        {!loading && rows.length > 0 && (
          <div className="bg-white rounded-lg border border-slate-200 divide-y">
            {rows.map(d => (
              <button key={d.id}
                data-testid={`dispute-row-${d.id}`}
                onClick={() => navigate(`/app/disputes/${d.id}`)}
                className="w-full text-left px-5 py-4 hover:bg-slate-50 flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <span className="font-medium text-slate-900">#{d.id}</span>
                    <span className="text-xs text-slate-500 uppercase tracking-wide">{d.subject_kind}</span>
                    <StateBadge state={d.state} />
                  </div>
                  <div className="text-sm text-slate-600 mt-1">
                    {d.reason_code} · filed {fmt(d.created_at)}
                  </div>
                  {d.sla_due_at && (
                    <div className="text-xs text-slate-400 mt-1 flex items-center gap-1">
                      <Clock className="h-3 w-3" /> SLA due {fmt(d.sla_due_at)}
                    </div>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}

// ============================================================================
// Filer detail (full evidence ledger + remediation visibility)
// ============================================================================

export function DisputeDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [dispute, setDispute] = useState(null);
  const [evidence, setEvidence] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newEvidence, setNewEvidence] = useState({
    evidence_type: "admin_note", title: "", description: "", external_url: ""
  });
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    try {
      const d = await api(`/me/disputes/${id}`);
      setDispute(d);
      const ev = await api(`/me/disputes/${id}/evidence`);
      setEvidence(ev.data || []);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const submitEvidence = async () => {
    if (!newEvidence.title.trim()) {
      toast.error("Title is required");
      return;
    }
    setSubmitting(true);
    try {
      await api(`/me/disputes/${id}/evidence`, {
        method: "POST",
        body: newEvidence,
      });
      toast.success("Evidence recorded");
      setNewEvidence({ evidence_type: "admin_note", title: "", description: "", external_url: "" });
      load();
    } catch (e) {
      toast.error(e.problem?.title || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const withdraw = async () => {
    if (!confirm("Withdraw this dispute? This is permanent.")) return;
    try {
      await api(`/me/disputes/${id}/withdraw`, {
        method: "POST",
        body: { reason: "filer_withdrew" },
      });
      toast.success("Dispute withdrawn");
      load();
    } catch (e) {
      toast.error(e.problem?.title || e.message);
    }
  };

  if (loading) return <Layout><div className="p-6 text-slate-500">Loading…</div></Layout>;
  if (!dispute) return null;
  const canWithdraw = ["proposed", "under_review"].includes(dispute.state);
  const canAddEvidence = ["proposed", "under_review"].includes(dispute.state);

  return (
    <Layout>
      <div className="max-w-5xl mx-auto p-6" data-testid="dispute-detail-page">
        <button onClick={() => navigate(-1)}
          data-testid="btn-back"
          className="text-sm text-slate-500 hover:text-slate-700 flex items-center gap-1 mb-4">
          <ChevronLeft className="h-4 w-4" /> Back
        </button>

        {/* Header */}
        <div className="bg-white rounded-lg border border-slate-200 p-6 mb-6">
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-3 mb-1">
                <h1 className="text-2xl font-semibold text-slate-900">Dispute #{dispute.id}</h1>
                <StateBadge state={dispute.state} />
              </div>
              <div className="text-sm text-slate-500">
                {dispute.subject_kind} · {dispute.reason_code} · filed {fmt(dispute.created_at)}
              </div>
            </div>
            {canWithdraw && (
              <button onClick={withdraw}
                data-testid="btn-withdraw-dispute"
                className="px-3 py-1.5 text-sm border border-rose-300 text-rose-700 rounded hover:bg-rose-50">
                Withdraw
              </button>
            )}
          </div>
          {dispute.filer_narrative && (
            <div className="mt-4 p-4 bg-slate-50 border-l-4 border-slate-300 rounded">
              <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">Your narrative</div>
              <div className="text-sm text-slate-700 whitespace-pre-wrap">{dispute.filer_narrative}</div>
            </div>
          )}
          {dispute.resolution_summary && (
            <div className="mt-4 p-4 bg-emerald-50 border-l-4 border-emerald-400 rounded"
                  data-testid="resolution-summary">
              <div className="text-xs uppercase tracking-wide text-emerald-700 mb-1 flex items-center gap-1">
                <CheckCircle2 className="h-3.5 w-3.5" /> Resolution
              </div>
              <div className="text-sm text-slate-700 whitespace-pre-wrap">{dispute.resolution_summary}</div>
            </div>
          )}
        </div>

        {/* Status timeline */}
        {dispute.transitions && dispute.transitions.length > 0 && (
          <DisputeTimeline transitions={dispute.transitions} />
        )}

        {/* Financial holds */}
        {dispute.financial_holds && dispute.financial_holds.length > 0 && (
          <Section title="Financial holds" icon={<Shield className="h-4 w-4" />} testId="section-holds">
            <ul className="divide-y divide-slate-100">
              {dispute.financial_holds.map(h => (
                <li key={h.id} data-testid={`hold-${h.id}`} className="py-2 flex justify-between text-sm">
                  <span>{h.hold_kind} · {h.state}</span>
                  <span className="text-slate-500">placed {fmt(h.placed_at)}</span>
                </li>
              ))}
            </ul>
          </Section>
        )}

        {/* Remediation history */}
        {dispute.remediation_actions && dispute.remediation_actions.length > 0 && (
          <Section title="Remediation history" icon={<RefreshCw className="h-4 w-4" />} testId="section-remediation">
            <ul className="divide-y divide-slate-100">
              {dispute.remediation_actions.map(a => (
                <li key={a.id} data-testid={`remediation-${a.id}`} className="py-2 flex justify-between text-sm">
                  <span>{a.action_kind}{a.amount ? ` — ${a.amount} ${a.currency}` : ""}</span>
                  <span className={`text-xs px-2 py-0.5 rounded ${
                    a.state === "applied" ? "bg-blue-50 text-blue-700" :
                    a.state === "rolled_back" ? "bg-rose-50 text-rose-700" : "bg-slate-100 text-slate-700"}`}>
                    {a.state}
                  </span>
                </li>
              ))}
            </ul>
          </Section>
        )}

        {/* Evidence ledger */}
        <Section title="Evidence" icon={<Paperclip className="h-4 w-4" />} testId="section-evidence">
          {evidence.length === 0 && <div className="text-sm text-slate-400">No evidence yet.</div>}
          <ul className="divide-y divide-slate-100">
            {evidence.map(e => <EvidenceItem key={e.id} ev={e} disputeId={id} />)}
          </ul>
          {canAddEvidence && (
            <div className="mt-4 border-t pt-4">
              <div className="text-sm font-medium text-slate-700 mb-2">Add evidence</div>
              <div className="grid grid-cols-1 gap-2">
                <select data-testid="select-evidence-type"
                  value={newEvidence.evidence_type}
                  onChange={e => setNewEvidence({ ...newEvidence, evidence_type: e.target.value })}
                  className="text-sm border-slate-300 rounded">
                  <option value="admin_note">Note</option>
                  <option value="external_reference">External reference</option>
                  <option value="document">Document description</option>
                  <option value="other">Other</option>
                </select>
                <input data-testid="input-evidence-title"
                  type="text" placeholder="Title"
                  value={newEvidence.title}
                  onChange={e => setNewEvidence({ ...newEvidence, title: e.target.value })}
                  className="text-sm border-slate-300 rounded" />
                <textarea data-testid="input-evidence-description"
                  placeholder="Description (optional)"
                  value={newEvidence.description}
                  onChange={e => setNewEvidence({ ...newEvidence, description: e.target.value })}
                  className="text-sm border-slate-300 rounded" rows={3} />
                <input data-testid="input-evidence-url"
                  type="text" placeholder="External URL (optional)"
                  value={newEvidence.external_url}
                  onChange={e => setNewEvidence({ ...newEvidence, external_url: e.target.value })}
                  className="text-sm border-slate-300 rounded" />
                <button data-testid="btn-submit-evidence"
                  disabled={submitting}
                  onClick={submitEvidence}
                  className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50">
                  Add to ledger
                </button>
                <div className="text-xs text-slate-400 flex items-center gap-1">
                  <Lock className="h-3 w-3" /> Evidence is append-only; once added it cannot be edited.
                </div>
              </div>
            </div>
          )}
        </Section>
      </div>
    </Layout>
  );
}

// ============================================================================
// Counterparty inbox + detail
// ============================================================================

export function CounterpartyInbox() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  useEffect(() => {
    (async () => {
      try {
        const r = await api("/me/counterparty/disputes?limit=50");
        setRows(r.data || []);
      } catch (e) {
        toast.error(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);
  return (
    <Layout>
      <div className="max-w-5xl mx-auto p-6" data-testid="counterparty-inbox-page">
        <Link to="/app/disputes"
              className="text-sm text-slate-500 hover:text-slate-700 mb-4 inline-flex items-center gap-1">
          <ChevronLeft className="h-4 w-4" /> My filed disputes
        </Link>
        <h1 className="text-2xl font-semibold text-slate-900 mt-2 mb-1">Disputes involving me</h1>
        <p className="text-sm text-slate-500 mb-6">
          Disputes filed by other parties that name you as a counterparty.
        </p>
        {loading && <div className="text-slate-500">Loading…</div>}
        {!loading && rows.length === 0 && (
          <div data-testid="empty-counterparty" className="text-center py-16 text-slate-400">
            <Inbox className="mx-auto h-10 w-10 mb-2" />
            <div>None right now.</div>
          </div>
        )}
        {!loading && rows.length > 0 && (
          <div className="bg-white rounded-lg border border-slate-200 divide-y">
            {rows.map(d => (
              <button key={d.id}
                data-testid={`counterparty-row-${d.id}`}
                onClick={() => navigate(`/app/counterparty/disputes/${d.id}`)}
                className="w-full text-left px-5 py-4 hover:bg-slate-50">
                <div className="flex items-center gap-3">
                  <span className="font-medium text-slate-900">#{d.id}</span>
                  <span className="text-xs text-slate-500 uppercase tracking-wide">{d.subject_kind}</span>
                  <StateBadge state={d.state} />
                </div>
                <div className="text-sm text-slate-600 mt-1">
                  Filed by {d.filer_role} · {fmt(d.created_at)}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}

export function CounterpartyDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [dispute, setDispute] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [evidence, setEvidence] = useState({
    evidence_type: "admin_note", title: "", description: "", external_url: ""
  });

  const load = async () => {
    try {
      const d = await api(`/me/counterparty/disputes/${id}`);
      setDispute(d);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const submitEvidence = async () => {
    if (!evidence.title.trim()) return toast.error("Title required");
    setSubmitting(true);
    try {
      await api(`/me/disputes/${id}/evidence`, {
        method: "POST",
        body: evidence,
      });
      toast.success("Evidence recorded");
      setEvidence({ evidence_type: "admin_note", title: "", description: "", external_url: "" });
      load();
    } catch (e) {
      toast.error(e.problem?.title || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <Layout><div className="p-6 text-slate-500">Loading…</div></Layout>;
  if (!dispute) return null;
  const isOpen = ["proposed", "under_review"].includes(dispute.state);

  return (
    <Layout>
      <div className="max-w-5xl mx-auto p-6" data-testid="counterparty-detail-page">
        <button onClick={() => navigate(-1)}
          data-testid="btn-back"
          className="text-sm text-slate-500 hover:text-slate-700 flex items-center gap-1 mb-4">
          <ChevronLeft className="h-4 w-4" /> Back
        </button>
        <div className="bg-white rounded-lg border border-slate-200 p-6 mb-6">
          <div className="flex items-center gap-3 mb-1">
            <h1 className="text-2xl font-semibold text-slate-900">Dispute #{dispute.id}</h1>
            <StateBadge state={dispute.state} />
          </div>
          <div className="text-sm text-slate-500">
            Filed by {dispute.filer_role} · {dispute.subject_kind} · {dispute.reason_code}
          </div>
          {dispute.filer_narrative && (
            <div className="mt-4 p-4 bg-slate-50 border-l-4 border-amber-400 rounded">
              <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">Allegation</div>
              <div className="text-sm text-slate-700 whitespace-pre-wrap">{dispute.filer_narrative}</div>
            </div>
          )}
          {dispute.resolution_summary && (
            <div className="mt-4 p-4 bg-emerald-50 border-l-4 border-emerald-400 rounded">
              <div className="text-xs uppercase tracking-wide text-emerald-700 mb-1 flex items-center gap-1">
                <CheckCircle2 className="h-3.5 w-3.5" /> Resolution
              </div>
              <div className="text-sm text-slate-700 whitespace-pre-wrap">{dispute.resolution_summary}</div>
            </div>
          )}
        </div>

        {dispute.financial_holds?.length > 0 && (
          <Section title="Financial holds" icon={<Shield className="h-4 w-4" />} testId="section-holds">
            <ul className="divide-y divide-slate-100">
              {dispute.financial_holds.map(h => (
                <li key={h.id} className="py-2 text-sm flex justify-between">
                  <span>{h.hold_kind} · {h.state}</span>
                  <span className="text-slate-500">{fmt(h.placed_at)}</span>
                </li>
              ))}
            </ul>
          </Section>
        )}

        {dispute.remediation_actions?.length > 0 && (
          <Section title="Resolution outcome" icon={<RefreshCw className="h-4 w-4" />} testId="section-remediation-outcome">
            <ul className="divide-y divide-slate-100">
              {dispute.remediation_actions.map(a => (
                <li key={a.id} className="py-2 text-sm flex justify-between">
                  <span>{a.action_kind}{a.amount ? ` — ${a.amount} ${a.currency}` : ""}</span>
                  <span className="text-slate-500">{fmt(a.applied_at)}</span>
                </li>
              ))}
            </ul>
          </Section>
        )}

        {dispute.credit_memos?.length > 0 && (
          <Section title="Credit memos" icon={<DollarSign className="h-4 w-4" />} testId="section-credit-memos">
            <ul className="divide-y divide-slate-100">
              {dispute.credit_memos.map(c => (
                <li key={c.id} data-testid={`credit-memo-${c.id}`} className="py-2 text-sm flex justify-between">
                  <span>#{c.id} · {c.reason_code}</span>
                  <span className="font-medium">{c.amount} {c.currency}</span>
                </li>
              ))}
            </ul>
          </Section>
        )}

        <Section title="Evidence" icon={<Paperclip className="h-4 w-4" />} testId="section-evidence-cp">
          {dispute.evidence?.length === 0 && <div className="text-sm text-slate-400">No evidence yet.</div>}
          <ul className="divide-y divide-slate-100">
            {(dispute.evidence || []).map(e => <EvidenceItem key={e.id} ev={e} disputeId={id} />)}
          </ul>
          {isOpen && (
            <div className="mt-4 border-t pt-4">
              <div className="text-sm font-medium text-slate-700 mb-2">Add your response evidence</div>
              <input data-testid="input-cp-evidence-title"
                type="text" placeholder="Title"
                value={evidence.title}
                onChange={e => setEvidence({ ...evidence, title: e.target.value })}
                className="text-sm border-slate-300 rounded w-full mb-2" />
              <textarea data-testid="input-cp-evidence-description"
                placeholder="Your account / response"
                value={evidence.description}
                onChange={e => setEvidence({ ...evidence, description: e.target.value })}
                className="text-sm border-slate-300 rounded w-full mb-2" rows={3} />
              <button data-testid="btn-cp-submit-evidence"
                disabled={submitting} onClick={submitEvidence}
                className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50">
                Add to ledger
              </button>
            </div>
          )}
        </Section>
      </div>
    </Layout>
  );
}

// ============================================================================
// Shared bits
// ============================================================================

function Section({ title, icon, children, testId }) {
  return (
    <div data-testid={testId} className="bg-white rounded-lg border border-slate-200 p-6 mb-6">
      <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-3 flex items-center gap-2">
        {icon} {title}
      </h2>
      {children}
    </div>
  );
}

function DisputeTimeline({ transitions }) {
  return (
    <div data-testid="section-timeline" className="bg-white rounded-lg border border-slate-200 p-6 mb-6">
      <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-3 flex items-center gap-2">
        <Clock className="h-4 w-4" /> Status timeline
      </h2>
      <ol className="relative border-l border-slate-200 pl-4 space-y-3">
        {transitions.map((t, i) => (
          <li key={i} data-testid={`timeline-row-${i}`} className="relative">
            <span className="absolute -left-[21px] top-1.5 h-3 w-3 rounded-full bg-blue-500" />
            <div className="text-sm">
              <span className="font-medium text-slate-900">{t.from_state || "initial"} → {t.to_state}</span>
              <span className="ml-2 text-xs text-slate-500">{fmt(t.occurred_at)} · {t.actor_kind}</span>
            </div>
            {t.reason_note && <div className="text-xs text-slate-500 mt-0.5">{t.reason_note}</div>}
          </li>
        ))}
      </ol>
    </div>
  );
}

function EvidenceItem({ ev, disputeId }) {
  const [downloading, setDownloading] = useState(false);
  const handleDownload = async () => {
    if (!ev.has_object) return;
    setDownloading(true);
    try {
      const r = await api(`/me/disputes/${disputeId}/evidence/${ev.id}/url`);
      window.open(r.url, "_blank");
    } catch (e) {
      toast.error(e.problem?.title || e.message);
    } finally {
      setDownloading(false);
    }
  };
  return (
    <li data-testid={`evidence-${ev.id}`} className="py-3">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <FileText className="h-4 w-4 text-slate-400" />
            <span className="text-sm font-medium text-slate-900">{ev.title}</span>
            <span className="text-xs text-slate-500">· {ev.evidence_type}</span>
            <span className="text-xs text-slate-400">· by {ev.uploaded_by_role}</span>
          </div>
          {ev.description && (
            <div className="text-sm text-slate-600 mt-1 whitespace-pre-wrap">{ev.description}</div>
          )}
          {ev.external_url && (
            <a href={ev.external_url} target="_blank" rel="noreferrer"
              className="text-sm text-blue-600 hover:underline mt-1 inline-block">
              {ev.external_url}
            </a>
          )}
          <div className="text-xs text-slate-400 mt-1">
            {fmt(ev.created_at)}{ev.sha256_hex ? ` · sha256: ${ev.sha256_hex.slice(0, 12)}…` : ""}
          </div>
        </div>
        {ev.has_object && (
          <button onClick={handleDownload} disabled={downloading}
            data-testid={`btn-download-evidence-${ev.id}`}
            className="text-xs text-blue-600 hover:underline disabled:opacity-50">
            Download
          </button>
        )}
        {ev.is_redacted && (
          <span data-testid={`badge-redacted-${ev.id}`}
            className="text-xs px-2 py-0.5 bg-rose-50 text-rose-700 rounded">
            REDACTED
          </span>
        )}
      </div>
    </li>
  );
}

export default DisputesInbox;
